<script setup>
import { computed, nextTick, onBeforeUnmount } from "vue"
import { useEventStore } from "@/stores/EventsStore"
import { useModalStore } from "@/stores/ModalStore"
import { useCustomerStore } from "@/stores/CustomersStore"
import { useServiceStore } from "@/stores/ServicesStore"
import { useCalendarStore } from "@/views/apps/calendar/useCalendarStore"
import { useReserveListStore } from "@/stores/ReserveListStore"
import { useDisplay } from "vuetify"
import { combineDateAndTime } from "@/utils/datetime"
import { formatPhone } from "@/utils/strings"
import { useI18n } from "vue-i18n"
import PointsDisplayer from "@/components/PointsDisplayer.vue"
import MobileAutocomplete from "@/components/MobileAutocomplete.vue"
/* ---------------------------- 👉 Props ---------------------------- */
const props = defineProps({
  showClientCardBtn: {
    type: Boolean,
    default: true,
  },
  showNewClientBtn: {
    type: Boolean,
    default: true,
  },
  showSendSmsBtn: {
    type: Boolean,
    default: true,
  },
  showCheckboxes: {
    type: Boolean,
    default: true,
  },
  rules: {
    type: Array,
    default: () => [],
  },
})

const { mobile, xs, sm, md, lg, lgAndDown } = useDisplay()
const { t } = useI18n()

/* ---------------------------- 👉 Stores ---------------------------- */
const eventStore = useEventStore()
const modalStore = useModalStore()
const customerStore = useCustomerStore()
const calendarStore = useCalendarStore()
const serviceStore = useServiceStore()
const reserveListStore = useReserveListStore()

/* ---------------------------- 👉 Variables ------------------------- */
// Ref to customer description element to check if truncated
const customerDescriptionRef = ref(null)
const isTruncated = ref(false)

const checkTruncation = async () => {
  await nextTick()
  const el = customerDescriptionRef.value
  if (el) {
    isTruncated.value = el.scrollHeight > el.clientHeight
  }
}
/* ---------------------------- 👉 Methods ---------------------------- */

// Check if string will be wrapped in textarea
const willTextBeWrappedInTextarea = (textAreaId, stringToCheck) => {
  const textarea = document.getElementById(textAreaId)
  console.log(textAreaId, stringToCheck)
  if(!textarea) return false
  const textareaStyle = window.getComputedStyle(textarea)
  const textareaWidth = parseInt(textareaStyle.width, 10)
  const textareaPaddingLeft = parseInt(textareaStyle.paddingLeft, 10)
  const textareaPaddingRight = parseInt(textareaStyle.paddingRight, 10)
  const textareaInnerWidth = textareaWidth - textareaPaddingLeft - textareaPaddingRight - 25 //25 is for clear button

  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')

  const style = window.getComputedStyle(textarea)
  const font = `
    ${style.fontStyle}
    ${style.fontVariant}
    ${style.fontWeight}
    ${style.fontSize}
    / ${style.lineHeight}
    ${style.fontFamily}
  `

  context.font = font

  const metrics = context.measureText(stringToCheck)
  console.log('metrics', metrics.width)
  console.log('textareaInnerWidth', textareaInnerWidth)

  return metrics.width > textareaInnerWidth

}
// Get formated string for customer vautocomplete mobile alternative component
const getCustomerTextForTextarea = (textAreaId, customer) => {
  if(!customer) return ''
  let returnString = customer.name + ' ' + customer.surname

  if(customer.phone) {
    //returnString += willTextBeWrappedInTextarea(textAreaId, returnString+' ()'+formatPhone(customer.phone)) ? '\r\n' : ' '
    //returnString += `(${formatPhone(customer.phone)})`
    let phoneString = formatPhone(customer.phone).replaceAll(' ', '\u00A0') // add "hard spaces"
    returnString += ' ('+phoneString+')'
  }

  return returnString
}

const handleCustomerSelection = async (customerObject) => {

  //console.log('customerObject', customerObject)

  // If no customer is selected, clear the last event visits
  // it happens when someone uses backspace to clear the input
  // or closes the chip
  if(!customerObject) {
    await nextTick()
    clearLastEventVisits()
    customerStore.clearSelectedCustomer()
    customerStore.clearCustomerStatistics()

    return
  }

  // If no customer is selected, but there is some text in search input
  // and this object is disabled that means somebody clicked on "Twa wyszukiwanie... (kliknij aby anulować)"
  if (customerObject && customerObject?.disabled) {
    abortRequestsGetThatStartsWith('/calendar/clients')
    await nextTick()
    customerStore.isLoading = false
    customerStore.clearSelectedCustomer()
    customerStore.customersArray = []
    setTimeout(async () => {
      customerStore.customersArray = []
    }, 50)

    return
  }

  // If no customer is selected, but there is some text in search input
  // clear the selected customer and open the modal to add a new customer
  if (customerObject && !customerObject?.salonerClientId>0) {
    customerStore.isLoading = false
    customerStore.clearSelectedCustomer()
    modalStore.openModal('addNewCustomer', 'selectCustomerAutocomplete')

    return
  }

  // If a customer is selected change prepayment method email and phone
  if(!eventStore.isPrepaymentSectionDisabled && eventStore.selectedPrepaymentMethod.id === 8) {
    eventStore.selectedPrepaymentMethod.email = customerObject.email || ''
    eventStore.selectedPrepaymentMethod.phone = customerObject.phone || ''
  }

  //Check if customer has other visits sam day
  if(eventStore.showFinalization && eventStore.checkForCustomersOtherVisits()) showWarningToast(t('customerHaveOtherVisitsThisDay'))

  //Refresh Vouchers and Tickets on customer change
  customerStore.getCustomerVouchersAndTickets(customerObject.salonerClientId).then(() => {}) // B: we don't have to wait for this function result here

  //Fill the selected services based on last visit, only when adding a fresh new visit
  if (!eventStore.selectedEvent.id && modalStore.modals.calendarEvent) {
    try {
      // Check if searched customer have any incoming visits
      const response = await $backend(`/calendar/clientLastServicesAndIncomingEvents`, {
        params: {
          client: eventStore.selectedCustomer?.ekey,
          employee: eventStore.selectedEmployee?.id,
          dateStart: eventStore.selectedEvent?.dateStart || null,
        },
        method: 'GET',
      })

      // Last event
      if(response.data.lastEvent) {
        // Get the service ekeys from the last event
        const lastServiceEkeys = response.data.lastEvent?.services?.map(service => service.ekey)
        if(!lastServiceEkeys) return

        // Get the employee ekey from the last event
        const lastEventEmployeeEkey = response.data.lastEvent?.employee.ekey
        if(!lastEventEmployeeEkey) return

        // Find matching services in the service store - match both service ekey and employee ekey
        customerStore.suggestedServicesForClient = serviceStore.servicesArray.filter(service =>
          lastServiceEkeys.includes(service.ekey) && service.employeeEkey === lastEventEmployeeEkey,
        )

      }
      // Incoming events
      customerStore.incomingClientVisits = response.data.incomingEvents || []


    }catch(error){
      console.error('Error fetching client last services:', error)
    }
  }

}

// Clear last event visits when input is cleared
const clearLastEventVisits = () => {

  if (eventStore.selectedServices) {
    // Make sure we're properly identifying services with isAddedFromLastVisit
    eventStore.selectedServices = eventStore.selectedServices.filter(service => {
      return service.isAddedFromLastVisit !== true
    })
  }
}

let clearSearchedCustomerTimer = null

const clearSearchedCustomer = () => {
  clearSearchedCustomerTimer = setTimeout(() => {
    customerStore.searchedCustomer = ''
  }, 500)
}


// Reverse wanted notification value couse backend logic is opposite (dumb)
// mm: Magda zmieniła, checkbox zaznaczony(nie wysyłaj) - true, niezaznaczony(wysyłaj) - false
const dontSendNotification = computed({
  get: () => eventStore.selectedEvent.wantedNotif,
  set: (value) => {
    eventStore.selectedEvent.wantedNotif = value
  },
})

const isMoreThan24Hours = computed(() => {
  if (!eventStore.selectedEvent?.dateStart || !eventStore.selectedEvent?.timeStart) return false

  try {
    const selectedDateTime = combineDateAndTime(eventStore.selectedEvent.dateStart, eventStore.selectedEvent.timeStart)
    const now = new Date()

    // Validate if combineDateAndTime returned a valid Date object
    if (!(selectedDateTime instanceof Date) || isNaN(selectedDateTime.getTime())) {
      console.error("Invalid date/time format detected")

      return false
    }

    const differenceInMilliseconds = selectedDateTime.getTime() - now.getTime()
    const differenceInHours = differenceInMilliseconds / (1000 * 60 * 60)

    // Event is more than 24 hours ahead in the future
    return differenceInHours > 24
  } catch (error) {
    console.error("Error calculating time difference:", error)

    return false
  }
})


onBeforeUnmount(() => {
  if (clearSearchedCustomerTimer) {
    clearTimeout(clearSearchedCustomerTimer)
  }
})
</script>

<template>
  <VRow dense>
    <!-- 👉 Customers -->
    <VCol cols="12">
      <!-- Desktop: VAutocomplete -->
      <VAutocomplete
        v-if="!mobile"
        id="selectCustomerAutocomplete"
        v-model="customerStore.selectedCustomer"
        class="autocomplete-force-inline"
        autocomplete="off"
        :loading="customerStore.isLoading && !modalStore.modals.customerCard && !modalStore.modals.addNewCustomer"
        :items="customerStore.customersArray"
        :item-title="item => item ? `${item.name} ${item.surname}`+ (item.phone ? ` (${formatPhone(item.phone)})` : '') : ''"
        :item-value="item => item.ekey"
        density="compact"
        color="secondary"
        :label="$t('search_for_customer')"
        :placeholder="$t('type_at_least_3chars')"
        hide-no-data
        :auto-select-first="customerStore.customersArray.length > 0 && !customerStore.customersArray[0].disabled"
        no-filter
        return-object
        chips
        closable-chips
        :disabled="!eventStore.allowSave || eventStore.isSaving || reserveListStore.isSaving"
        :multiple="false"
        :rules="rules"
        @update:search="customerStore.fetchCustomers"
        @update:model-value="handleCustomerSelection"
        @input="customerStore.clearSelectedCustomer"
        @blur="clearSearchedCustomer"
      >
        <template #prepend-inner>
          <VIcon
            size="24"
            class=""
            color="primary"
            icon="ri-user-line"
          />
        </template>
        <template #append-inner>
          <span
            v-if="!mobile && (customerStore.isCustomerBlacklisted || eventStore.selectedCustomer?.blacklisted === '1')"
            class="d-flex"
          >
            <VChip
              size="x-small"
              data-testid="customer_selector_client_blacklisted_indicator"
            > <small class="mr-1">{{ $t('blacklisted') }}</small>
              <VIcon
                color="error"
                size="small"
                icon="ri-alarm-warning-fill"
              />
            </VChip>

          </span>
        </template>
      </VAutocomplete>

      <!-- Mobile: MobileAutocomplete -->
      <MobileAutocomplete
        v-else
        v-model="customerStore.selectedCustomer"
        :loading="customerStore.isLoading && !modalStore.modals.customerCard && !modalStore.modals.addNewCustomer"
        :items="customerStore.customersArray"
        :item-title="item => getCustomerTextForTextarea('customerSearchTextareaOnMobile', item)"
        :item-value="item => item.ekey"
        text-area-id="customerSearchTextareaOnMobile"
        :label="$t('search_for_customer')"
        :placeholder="$t('type_at_least_3chars')"
        :inner-label="$t('search_for_customer')"
        :inner-placeholder="$t('type_at_least_3chars')"
        hide-no-data
        no-filter
        return-object
        :disabled="!eventStore.allowSave || eventStore.isSaving || reserveListStore.isSaving"
        :rules="rules"
        @update:search="customerStore.fetchCustomers"
        @update:model-value="handleCustomerSelection"
        @blur="clearSearchedCustomer"
      >
        <template #prepend-inner>
          <VIcon
            size="24"
            color="primary"
            icon="ri-user-line"
          />
        </template>
      </MobileAutocomplete>

      <div class="d-flex justify-space-between align-center flex-wrap">
        <small
          v-if="mobile && (customerStore.isCustomerBlacklisted || eventStore.selectedCustomer?.blacklisted === '1')"
          class="d-flex ml-2"
        >
          <span
            class="ml-2 mt-1"
            data-testid="client_card_mobile_client_blacklisted_indicator"
          > <span class="mr-1">{{ $t('blacklisted') }}</span>
            <VIcon
              color="error"
              size="small"
              icon="ri-alarm-warning-fill"
            />
          </span>
        </small>
        <small
          v-if="eventStore.selectedCustomer?.salonerClientId && (!customerStore.isLoading || modalStore.modals.customerCardDialog || modalStore.modals.addNewCustomer)"
          class="ml-4 d-flex align-center align-content-center"
        >
          <span>{{ $t('noShowsRatio') }}: {{ customerStore.customerStatistics.factor }}%</span>

          <span
            class="ml-1"
            style="margin-top: -2px;"
          >
            <VIcon
              icon="ri-question-line"
              size="medium"
              color="info"
            />
            <VTooltip
              activator="parent"
              :open-on-hover="!mobile"
              :open-on-click="mobile"
            >
              {{ $t('amountOfAllVisits') }}: {{ customerStore.customerStatistics.allVisits }}<br>
              {{ $t('amountOfNoShows10') }}: {{ customerStore.customerStatistics.noShow }}
            </VTooltip>
          </span>

        </small>

        <!-- Punkty przy kliencie pokazywać zawsze (jeżeli są), nawet jak program jest OFF-->
        <!-- Nawet jak klient ma 0 punktów to pokazujemy  chyba że program lojalnościowy jest OFF -->
        <!-- Nie pokazywać bez wybranego klienta -->
        <small
          v-if="(customerStore.selectedCustomer && (customerStore.customerLoyaltyPoints > 0 || calendarStore.loyaltyProgramSettings.enabled))"
          class="ml-4"
        >
          <PointsDisplayer
            :points="customerStore.customerLoyaltyPoints"
            :text="$t('points_held')"
          />
        </small>
        <small
          v-if="customerStore.selectedCustomer?.description || customerStore.selectedCustomer?.notes"
          class="ml-4 font-italic"
          style="width: 100%"
        >
          <div class="d-flex ga-1 mb-1">
            <VIcon icon="ri-user-voice-line mr-1" />
            <span
              ref="customerDescriptionRef"
              class="truncate-2-lines line-height-normal"
              @mouseenter="checkTruncation"
            >
              {{ customerStore.selectedCustomer.description || customerStore.selectedCustomer?.notes }}
            </span>
          </div>
          <VMenu
            v-if="isTruncated"
            activator="parent"
            open-on-click
          >
            <VSheet
              class="pa-2 xs-font-size"
              style="max-width: 300px; white-space: pre-wrap;"
            >
              {{ customerStore.selectedCustomer.description || customerStore.selectedCustomer?.notes }}
            </VSheet>
          </VMenu>
        </small>
      </div>
      <div
        v-if="eventStore.selectedEvent.id && eventStore.selectedEvent.registerType"
        class="ml-4 mt-n1"
      >
        <small>
          {{ $t('sheduledBy') }}: <strong>{{ eventStore.selectedEvent.registerType }}</strong>
        </small>
      </div>
    </VCol>

    <VCol cols="12">
      <div>
        <div class="d-flex ga-2 w-100 mt-2">
          <VBtn
            v-if="showClientCardBtn"
            id="showCustomerCardBtn"
            size="small"
            class="flex-fill"
            color="secondary"
            max-width="50%"
            variant="flat"
            prepend-icon="ri-folder-user-line"
            :disabled="!customerStore.selectedCustomer || eventStore.isSaving || reserveListStore.isSaving"
            @click="modalStore.openModal('customerCardDialog', 'showCustomerCardBtn');"
          >
            <template #prepend>
              <VIcon size="24" />
            </template>
            <span class="xs-font-size">
              {{ mobile ? 'Karta' : $t('customer_card') }}
            </span>
          </VBtn>
          <VBtn
            id="showCustomerCardBtn"
            size="small"
            variant="flat"
            color="secondary"
            :disabled="!customerStore.selectedCustomer || eventStore.isSaving || reserveListStore.isSaving"
            :href="`/clients?clientId=${customerStore.selectedCustomer?.salonerClientId}`"
            target="_blank"
            rel="noopener noreferrer"
          >
            <template #prepend>
              <VIcon
                size="24"
                class="mr-n2"
                icon="ri-folder-shared-line"
              />
              <VTooltip activator="parent">
                {{ $t('openCustomerCardInNewTab') }}
              </VTooltip>
            </template>
          </VBtn>
          <VBtn
            v-if="showNewClientBtn"
            id="addNewCustomerBtn"
            size="small"
            class="flex-fill"
            color="secondary"
            max-width="50%"
            variant="flat"
            :disabled="!eventStore.allowSave || eventStore.isSaving || reserveListStore.isSaving"
            prepend-icon="ri-user-add-line"
            @click="modalStore.openModal('addNewCustomer', 'addNewCustomerBtn'); "
          >
            <template #prepend>
              <VIcon size="24" />
            </template>
            <span class="xs-font-size">
              {{ mobile ? 'Nowy' : $t('new_customer') }}
            </span>
          </VBtn>

          <VBtn
            v-if="showSendSmsBtn"
            id="showSendSmsToCustomerBtn"
            class="flex-fill"
            color="secondary"
            :disabled="!customerStore.selectedCustomer || !customerStore.selectedCustomer?.phone || eventStore.isSaving"
            size="small"
            variant="flat"
            prepend-icon="ri-mail-send-line"
            @click="modalStore.openModal('sendSmsToCustomer', 'showSendSmsToCustomerBtn')"
          >
            <template #prepend>
              <VIcon
                :class="[{'mr-n2' : lgAndDown}]"
                size="24"
              />
              <VTooltip
                v-if="lgAndDown"
                activator="parent"
              >
                {{ $t('send_sms') }}
              </VTooltip>
            </template>
            <span
              v-if="!xs && (sm || md) && !lg"
              class="xs-font-size ml-2"
            >
              {{ mobile ? 'SMS' : $t('send_sms') }}
            </span>
          </VBtn>
        </div>

        <div
          v-if="showCheckboxes"
          class="d-flex flex-wrap ga-2 mt-2"
        >
          <VCheckbox
            v-model="eventStore.selectedEvent.clientConfirmed"
            :disabled="!eventStore.allowSave || eventStore.isSaving"
            density="compact"
            color="primary"
            hide-details
            class="my-1"
            :label="$t('client_confirmed_visit')"
          />
          <VCheckbox
            v-if="calendarStore.viewSettings.clientInSalonStatus && eventStore.selectedEvent?.dateStart === (new Date()).toISOString().slice(0, 10)"
            v-model="eventStore.selectedEvent.clientInSalon"
            :disabled="!eventStore.allowSave || eventStore.isSaving"
            density="compact"
            color="primary"
            hide-details
            class="my-1"
            :label="$t('isPresent')"
          />
          <VCheckbox
            v-if="customerStore.selectedCustomer && isMoreThan24Hours"
            v-model="dontSendNotification"
            :disabled="!eventStore.allowSave || eventStore.isSaving"
            density="compact"
            color="primary"
            hide-details
            class="my-1"
            :label="$t('doNotSendNotification')"
          />
        </div>
      </div>
    </VCol>
  </VRow>
</template>
